import { Card, Col, Row, Skeleton, Tag } from "antd";
import { Fragment } from "react/jsx-runtime";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import "highcharts/modules/drilldown";
import { useEffect, useMemo, useState } from "react";
import { useGetDashboardExctuiveSummaryData } from "features/dashboard/use-cases/get-exctuive-summary";


export const ParticipantperMonth = ({ dateRange, selectedYear, selectedCategory, selectedEvent, chartRef }: any) => {
    const [selectedMonths, setSelectedMonths] = useState<string[]>([]);
    const monthShortNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sept", "Oct", "Nov", "Dec"];
    const monthFullNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const barColors = [
        '#E55050',
        '#008048',
        '#35B1E8',
        '#FFA51F',
        '#FEBEC8',
        '#4C5167',
        '#008044',
        '#35B1E8',
    ];;
    const monthListPayload = useMemo(
        () => selectedMonths.map((shortMonth) => monthShortNames.indexOf(shortMonth) + 1).join(","),
        [selectedMonths]
    );

    const { data: ParticipantGraph, isFetching: participantloading } = useGetDashboardExctuiveSummaryData({
        Flag: "Participant",
        FromDate: dateRange?.[0]?.format("YYYY-MM-DD") || "",
        ToDate: dateRange?.[1]?.format("YYYY-MM-DD") || "",
        monthlist: monthListPayload || undefined,
        Year: selectedYear,
        CategoryId: selectedCategory,
        EventId: selectedEvent
    } as any);

    const handleMonthToggle = (month: string) => {
        setSelectedMonths((prev) =>
            prev.includes(month) ? prev.filter((m) => m !== month) : [...prev, month]
        );
    };

    // ✅ Monthly Graph Data
    const monthlyDataFromApi =
        ParticipantGraph?.GraphData?.map((item: any) => ({
            name: monthShortNames[item.GMonth - 1],
            y: item.TotalParticipants || 0,
            drilldown: monthFullNames[item.GMonth - 1],
        })) || [];

    // ✅ Drilldown Data (Daily)
    const drilldownSeriesFromApi =
        ParticipantGraph?.DrilldownData?.length
            ? Object.values(
                ParticipantGraph.DrilldownData.reduce((acc: any, day: any) => {
                    const monthFull = monthFullNames[day.GMonth - 1];
                    if (!acc[monthFull]) {
                        acc[monthFull] = {
                            id: monthFull,
                            name: `Daily Participants - ${monthFull}`,
                            type: "column",
                            colors: barColors,
                            data: [],
                        };
                    }
                    acc[monthFull].data.push([
                        `${day.GDay} ${monthShortNames[day.GMonth - 1]}`,
                        day.TotalParticipants || 0,
                    ]);

                    return acc;
                }, {})
            )
            : [];

    // ✅ Filtered or All Data
    const baseSeriesData =
        selectedMonths.length === 0
            ? monthlyDataFromApi
            : selectedMonths.map((shortMonth) => {
                const monthIndex = monthShortNames.indexOf(shortMonth);
                const total =
                    ParticipantGraph?.DrilldownData?.filter((d: any) => d.GMonth - 1 === monthIndex)
                        .reduce((sum: number, d: any) => sum + (d.TotalParticipants || 0), 0) || 0;

                return { name: shortMonth, y: total };
            });

    // ✅ Chart Options
    const chartOptions: Highcharts.Options = {
        chart: { type: "column" },
        title: {
            text:
                selectedMonths.length === 0
                    ? "Participants per Month"
                    : `Participants for: ${selectedMonths.join(", ")}`,
        },
        lang: { noData: "No data found" },
        xAxis: {
            type: "category",
            labels: {
                style: {
                    fontSize: "10px",
                    color: "black",        // ✅ month names black
                    textDecoration: "none" // ✅ no underline
                },
            },
        },
        yAxis: {
            title: { text: "Participants", style: { fontSize: "11px", color: "black" } },
            labels: { style: { fontSize: "11px", color: "black" } },
        },
        legend: { enabled: false },
        colors: barColors, // ✅ Apply alternating colors globally
        plotOptions: {
            series: {
                borderWidth: 0,
                colorByPoint: true,
                dataLabels: {
                    enabled: true,
                    format: "{point.y:.0f}", // ✅ only top value
                    style: { color: "black", fontSize: "11px", fontWeight: "bold" },
                },
            },
        },
        credits: {
            enabled: false
        },
        tooltip: {
            headerFormat: '<span style="font-size:11px">{series.name}</span><br>',
            pointFormat: '<span style="color:{point.color}">{point.name}</span>: <b>{point.y}</b><br/>',
        },
        series: [
            {
                name:
                    selectedMonths.length === 0
                        ? "Monthly Participants"
                        : `Total (${selectedMonths.length} month${selectedMonths.length > 1 ? "s" : ""})`,
                type: "column",
                data: baseSeriesData,
            },
        ],
        ...(selectedMonths.length === 0 && {
            drilldown: {
                series: drilldownSeriesFromApi,
                activeAxisLabelStyle: {
                    textDecoration: "none",
                    color: "black",
                    fontSize: "10px"   // ✅ make it small
                },
                activeDataLabelStyle: {
                    textDecoration: "none",
                    color: "black",
                    fontSize: "10px"   // ✅ make it small
                }
            },
        }),
    } as any;
    useEffect(() => {
        const chart = chartRef.current?.chart;
        if (chart) {
            if (participantloading) {
                chart.showLoading("Loading data...");
            } else {
                chart.hideLoading();
            }
        }
    }, [participantloading]);

    return (
        <Fragment>
            <Card>
                <Row gutter={[16, 16]} className="month_tabs">
                    {monthShortNames.map((month) => (
                        <Col key={month}>
                            <Tag
                                color={selectedMonths.includes(month) ? "yellow" : "default"}
                                style={{ cursor: "pointer" }}
                                onClick={() => handleMonthToggle(month)}
                            >
                                {month}
                            </Tag>
                        </Col>
                    ))}
                </Row>
                {participantloading ? <Skeleton /> :
                    <HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartRef} />
                }
            </Card>
        </Fragment>
    );
};
